import { motion } from 'framer-motion';
import { ReactComponent as GhostLogo } from '../../assets/logo-4.svg';
import './loading-pet.css';

/* Formas Tetris (tetrominoes): [col, row] em unidades de célula */
const TETRIS_SHAPES: [number, number][][] = [
  [[0, 0], [1, 0], [0, 1], [1, 1]],           // O
  [[0, 0], [0, 1], [0, 2], [0, 3]],           // I
  [[0, 0], [0, 1], [0, 2], [1, 2]],           // L
  [[1, 0], [1, 1], [1, 2], [0, 2]],           // J
  [[0, 0], [1, 0], [2, 0], [1, 1]],           // T
  [[1, 0], [2, 0], [0, 1], [1, 1]],           // S
  [[0, 0], [1, 0], [1, 1], [2, 1]],           // Z
];

const CELL_SIZE = 5;

interface LoadingPetProps {
  size?: 'small' | 'medium' | 'large';
  message?: string;
}

function LoadingPet({ size = 'medium', message }: LoadingPetProps) {
  // Colunas de peças Tetris caindo (esquerda e direita do monstrinho)
  const tetrisColumns = Array.from({ length: 8 }, (_, colIndex) => {
    const shapeIndex = colIndex % TETRIS_SHAPES.length;
    const shape = TETRIS_SHAPES[shapeIndex];
    return {
      id: colIndex,
      shape,
      left: `${(colIndex / 8) * 100}%`,
      delay: colIndex * 0.2 + Math.random() * 0.5,
      duration: 1.8 + Math.random() * 0.8
    };
  });

  return (
    <div className={`loading-pet-container loading-pet-${size}`}>
      {/* Chuva de peças Tetris (ambos os lados do monstrinho) */}
      <div className="loading-pet-tetris-rain">
        {tetrisColumns.map((col) => (
          <motion.div
            key={col.id}
            className="loading-tetris-column"
            style={{ left: col.left }}
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 120, opacity: [0, 0.6, 0.6, 0] }}
            transition={{
              duration: col.duration,
              delay: col.delay,
              repeat: Infinity,
              ease: "linear"
            }}
          >
            <div className="loading-tetris-piece">
              {col.shape.map(([c, r], i) => (
                <span
                  key={i}
                  className="loading-tetris-cell"
                  style={{
                    left: c * CELL_SIZE,
                    top: r * CELL_SIZE,
                    width: CELL_SIZE,
                    height: CELL_SIZE
                  }}
                />
              ))}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Ghost Pet */}
      <motion.div 
        className="loading-pet-ghost"
        animate={{ 
          y: [0, -8, 0],
          scale: [1, 1.05, 1],
        }}
        transition={{ 
          duration: 1.5, 
          repeat: Infinity, 
          ease: "easeInOut" 
        }}
      >
        <GhostLogo className="loading-pet-svg" />
      </motion.div>

      {/* Loading dots */}
      <div className="loading-pet-dots">
        <motion.span
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1, repeat: Infinity, delay: 0 }}
        >●</motion.span>
        <motion.span
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
        >●</motion.span>
        <motion.span
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
        >●</motion.span>
      </div>

      {/* Optional message */}
      {message && <p className="loading-pet-message">{message}</p>}
    </div>
  );
}

export default LoadingPet;
